import { apiService } from './apiService';

export interface ConnectInstance {
  id: number;
  client_id: number;
  domain: string;
  username?: string;
  db_name?: string;
  db_user?: string;
  status: string;
  current_step?: string;
  error_message?: string;
  creation_log?: string;
  certificate_expires_at?: string;
  last_health_check?: string;
  health_status?: string;
  created_at: string;
  updated_at: string;
}

export interface InstanceStatus {
  id: number;
  status: string;
  current_step?: string;
  error_message?: string;
  creation_log?: string;
  updated_at?: string;
}

export interface ConnectInstanceCreate {
  client_id: number;
  domain: string;
  sending_email_name?: string;
  sending_email_address?: string;
  create_dns_record?: boolean;
  configure_ses?: boolean;
}

class ConnectInstanceService {
  async getAll(): Promise<ConnectInstance[]> {
    const response = await apiService.get<ConnectInstance[]>('/api/admin/connect-instances');
    return response.data;
  }

  async getOne(id: number): Promise<ConnectInstance> {
    const response = await apiService.get<ConnectInstance>(`/api/admin/connect-instances/${id}`);
    return response.data;
  }

  async create(data: ConnectInstanceCreate): Promise<ConnectInstance> {
    const response = await apiService.post<ConnectInstance>('/api/admin/connect-instances', data);
    return response.data;
  }

  async checkHealth(id: number): Promise<{ health_status: string; last_health_check: string; certificate_expires_at?: string }> {
    const response = await apiService.get<{ health_status: string; last_health_check: string; certificate_expires_at?: string }>(
      `/api/admin/connect-instances/${id}/health`
    );
    return response.data;
  }

  async checkCertificate(id: number): Promise<{ certificate_expires_at?: string; has_certificate: boolean }> {
    const response = await apiService.get<{ certificate_expires_at?: string; has_certificate: boolean }>(
      `/api/admin/connect-instances/${id}/certificate`
    );
    return response.data;
  }

  async renewCertificate(id: number): Promise<{ status: string; message: string; certificate_expires_at?: string }> {
    const response = await apiService.post<{ status: string; message: string; certificate_expires_at?: string }>(
      `/api/admin/connect-instances/${id}/renew-certificate`
    );
    return response.data;
  }

  async getStatus(id: number): Promise<InstanceStatus> {
    const response = await apiService.get<InstanceStatus>(
      `/api/admin/connect-instances/${id}/status`
    );
    return response.data;
  }

  async clearLog(id: number): Promise<{ message: string; instance_id: number }> {
    const response = await apiService.delete<{ message: string; instance_id: number }>(
      `/api/admin/connect-instances/${id}/log`
    );
    return response.data;
  }

  async retry(id: number): Promise<{ message: string; instance_id: number }> {
    const response = await apiService.post<{ message: string; instance_id: number }>(
      `/api/admin/connect-instances/${id}/retry`
    );
    return response.data;
  }

  async cleanup(id: number): Promise<{ message: string; instance_id: number }> {
    const response = await apiService.post<{ message: string; instance_id: number }>(
      `/api/admin/connect-instances/${id}/cleanup`
    );
    return response.data;
  }

  async delete(id: number): Promise<{ message: string; instance_id: number }> {
    const response = await apiService.delete<{ message: string; instance_id: number }>(
      `/api/admin/connect-instances/${id}`
    );
    return response.data;
  }

  async stop(id: number): Promise<{ message: string; instance_id: number; status: string }> {
    const response = await apiService.post<{ message: string; instance_id: number; status: string }>(
      `/api/admin/connect-instances/${id}/stop`
    );
    return response.data;
  }

  async runStep(id: number, stepName: string): Promise<{ message: string; instance_id: number; domain: string; step: string }> {
    const response = await apiService.post<{ message: string; instance_id: number; domain: string; step: string }>(
      `/api/admin/connect-instances/${id}/run-step/${stepName}`
    );
    return response.data;
  }

  async reinstallFromStep(id: number, stepName: string): Promise<{ message: string; instance_id: number; domain: string; starting_step: string }> {
    const response = await apiService.post<{ message: string; instance_id: number; domain: string; starting_step: string }>(
      `/api/admin/connect-instances/${id}/reinstall-from/${stepName}`
    );
    return response.data;
  }

  async clearCache(id: number): Promise<{ status: string; message: string }> {
    const response = await apiService.post<{ status: string; message: string }>(
      `/api/admin/connect-instances/${id}/clear-cache`
    );
    return response.data;
  }

  async checkARecord(domain: string): Promise<{ 
    a_record_exists: boolean; 
    is_managed_in_route53: boolean; 
    hosted_zone_id?: string; 
    current_ip?: string; 
    ip_matches?: boolean;
    can_manage?: boolean;
    resolved_ip?: string;
    available_zones?: string[];
    error?: string 
  }> {
    const response = await apiService.post<{ 
      a_record_exists: boolean; 
      is_managed_in_route53: boolean; 
      hosted_zone_id?: string; 
      current_ip?: string; 
      ip_matches?: boolean;
      can_manage?: boolean;
      resolved_ip?: string;
      available_zones?: string[];
      error?: string 
    }>('/api/admin/connect-instances/check-a-record', { domain });
    return response.data;
  }

  async createARecord(domain: string, ip_address: string): Promise<{
    success: boolean;
    message: string;
    change_id?: string;
    error?: string
  }> {
    const response = await apiService.post<{
      success: boolean;
      message: string;
      change_id?: string;
      error?: string
    }>('/api/admin/connect-instances/create-a-record', { domain, ip_address });
    return response.data;
  }

  async checkEmailDomain(domain: string): Promise<{
    is_managed_in_route53: boolean;
    hosted_zone_id?: string;
    can_manage?: boolean;
    error?: string;
  }> {
    const response = await apiService.post<{
      is_managed_in_route53: boolean;
      hosted_zone_id?: string;
      can_manage?: boolean;
      error?: string;
    }>('/api/admin/connect-instances/check-email-domain', { domain });
    return response.data;
  }

  async configureSes(domain: string): Promise<{
    success: boolean;
    message: string;
    dkim_records?: Array<{ name: string; value: string }>;
    mail_from_domain?: string;
    error?: string;
  }> {
    const response = await apiService.post<{
      success: boolean;
      message: string;
      dkim_records?: Array<{ name: string; value: string }>;
      mail_from_domain?: string;
      error?: string;
    }>('/api/admin/connect-instances/configure-ses', { domain });
    return response.data;
  }
}

export const connectInstanceService = new ConnectInstanceService();




